import React, { useMemo, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import SelectCst from "@components/InputCst/SelectCst";
import SortMenuCst from "@components/SortMenuCst";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import FiltersAccordionToggle from "@components/FiltersAccordionCst/FiltersAccordionToggle";
import { AddItemsSelectionMode, AddItemsTabConfig } from "./types";

const DEFAULT_PAGE_SIZE = 16;

interface SortOption {
  value: string;
  label: string;
}

interface AddItemsTabBodyProps<T> {
  tab: AddItemsTabConfig<T>;
  selectionMode: AddItemsSelectionMode;
  allowDeselect?: boolean;
  selectedIds: Set<string | number>;
  onToggle: (item: T, next: boolean) => void;
  excludeIds?: (string | number)[];
  sortOptions?: SortOption[];
  sort: string;
  onSortChange: (next: string) => void;
  onCreate?: () => void;
  createLabel?: string;
}

function AddItemsTabBody<T>({
  tab,
  selectionMode,
  allowDeselect,
  selectedIds,
  onToggle,
  excludeIds,
  sortOptions,
  sort,
  onSortChange,
  onCreate,
  createLabel,
}: AddItemsTabBodyProps<T>) {
  const { t } = useTranslation("add-items-modal");
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const [searchSubmitted, setSearchSubmitted] = useState("");

  const [pageSize, setPageSize] = useState<number>(
    tab.pageSize ?? DEFAULT_PAGE_SIZE,
  );
  const filters = tab.useFiltersDefs?.() ?? [];

  const { items, totalItems, totalPages, isLoading } = tab.useDataHook({
    page,
    pageSize,
    search: searchSubmitted || undefined,
    sort: sort || undefined,
  });

  const visibleItems = useMemo(() => {
    if (!excludeIds || excludeIds.length === 0) return items;
    const exclude = new Set(excludeIds.map(String));
    return items.filter((it) => !exclude.has(String(tab.getId(it))));
  }, [items, excludeIds, tab]);

  const handleSearch = (value: string) => {
    setSearchSubmitted(value);
    setPage(1);
  };

  const handleToggle = (id: string | number, next: boolean) => {
    const found = visibleItems.find(
      (it) => String(tab.getId(it)) === String(id),
    );
    if (found) onToggle(found, next);
  };

  const filtersSlot = (
    <>
      {filters.map((f) => (
        <Box
          key={f.id}
          sx={{
            width: 160,
            flexShrink: 0,
            "& .MuiFormControl-root": {
              minWidth: "0 !important",
              width: "100% !important",
            },
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
          }}
        >
          <SelectCst
            id={f.id}
            label={f.label}
            value={f.value}
            options={f.options}
            onChange={(v: any) => {
              f.onChange(String(v ?? ""));
              setPage(1);
            }}
          />
        </Box>
      ))}

      {onCreate && (
        <Box
          component="button"
          type="button"
          onClick={onCreate}
          sx={{
            cursor: "pointer",
            padding: "8px 16px",
            borderRadius: "8px",
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: "transparent",
            color: theme.palette.text.primary,
            fontSize: "13px",
            fontWeight: 700,
            fontFamily: "inherit",
            flexShrink: 0,
          }}
        >
          {createLabel ?? String(t("create") ?? "Crea")}
        </Box>
      )}
    </>
  );

  const sortSlot =
    sortOptions && sortOptions.length > 0 ? (
      <SortMenuCst
        id={`add-items-sort-${tab.key}`}
        value={sort}
        options={sortOptions}
        onChange={(next) => onSortChange(String(next ?? ""))}
      />
    ) : undefined;

  const accordion = tab.filtersAccordion;

  const accordionSlot = tab.renderFiltersAccordion ? (
    tab.renderFiltersAccordion({ onApplied: () => setPage(1) })
  ) : accordion ? (
    <FiltersAccordionToggle
      pageId={accordion.pageId}
      value={accordion.value}
      onApply={(next) => {
        accordion.onApply(next);
        setPage(1);
      }}
      excludeKeys={accordion.excludeKeys}
      filterVisibility={accordion.filterVisibility}
    />
  ) : undefined;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <SearchableMultiSelectListCst<T>
        selectionMode={selectionMode}
        allowDeselect={allowDeselect}
        rowVariant={tab.rowVariant ?? "boxed"}
        items={visibleItems}
        getId={tab.getId}
        renderRow={({ item, selected, onToggle: rowOnToggle }) =>
          tab.renderRow({
            item,
            selected,
            onToggle: rowOnToggle,
            selectionMode,
          })
        }
        headerSlot={accordionSlot}
        selectedCountEndSlot={sortSlot}
        selectedIds={selectedIds}
        onToggle={handleToggle}
        initialSearchValue={searchSubmitted}
        onSearchSubmit={handleSearch}
        searchPlaceholder={
          tab.searchPlaceholder ?? String(t("search-placeholder") ?? "Cerca")
        }
        searchButtonLabel={
          tab.searchButtonLabel ?? String(t("search-button") ?? "Cerca")
        }
        filtersSlot={filtersSlot}
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={(next) => {
          setPageSize(next);
          setPage(1);
        }}
        isLoading={isLoading}
        showSelectedCount={selectionMode === "multi"}
        selectedCountLabel={String(
          t("selected-count", { count: selectedIds.size }) ??
            `${selectedIds.size} elementi selezionati`,
        )}
        emptyLabel={
          tab.emptyLabel ?? String(t("empty") ?? "Nessun elemento trovato")
        }
      />
    </Box>
  );
}

export default AddItemsTabBody;
